import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import Chart, { ChartConfiguration, ChartConfigurationCustomTypesPerDataset, ChartMeta } from 'chart.js/auto';
import { getRelativePosition } from 'chart.js/helpers';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { getMinMaxDate } from '../../services/assessmentsHistory.functions';
import { AssessmentChartDataRow, AssessmentChartDataSets, Patient, SubTestChartDataSet } from '../../services/model';
import { PointStyleComponent } from '../chart-utils/point-style.component';
import { customPointCanvasLeft, customPointCanvasRight } from '../chart-utils/point-styles.functions';
import { findLabel, getLabelHitBoxes, highlightPlugin, splitTextByMaxChars } from '../chart-utils/utils.functions';

@Component({
  selector: 'app-line-chart-multi',
  standalone: true,
  imports: [PointStyleComponent],
  templateUrl: './line-chart-multi.component.html',
  styleUrl: './line-chart-multi.component.scss',
})
export class LineChartMultiComponent implements OnInit {
  @Input() data!: Array<AssessmentChartDataRow> | undefined;
  @Input() dataFull!: Signal<AssessmentChartDataSets | undefined>;
  @Input() chartId!: string;
  @Input() patient!: Patient;
  selectedIndex = input(0);
  @Output() indexClicked = new EventEmitter<number>(true);
  @ViewChild('canvas', { read: ElementRef, static: true }) canvas!: ElementRef<any>;
  public chart: any;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    // console.log('app-line-chart-multi construktor');

    dayjs.locale('de');

    // getPersistedConfig()
    const subType: 'all' | 'grasp' | 'grip' | 'pinch' | 'gross' = 'all';

    effect(() => {
      this.dataFull();
      if (this.chart) {
        this.createChart(subType);
        this.cdr.detectChanges();
        setTimeout(() => {
          // P.S. WTF!!!
          this.createChart(subType);
          this.cdr.detectChanges();
        }, 50);
      }
    });
    effect(() => {
      const newIndex = this.selectedIndex();
      // console.log('chriss effect' + newIndex);
      this.updateChart(newIndex);
    });
  }

  ngOnInit(): void {
    const subType: 'all' | 'grasp' | 'grip' | 'pinch' | 'gross' = 'all';
    setTimeout(() => {
      this.createChart(subType);
      this.cdr.detectChanges();
    }, 50);
  }

  // Aktualisiere den Chart, wenn der Highlight-Index geändert wird
  updateChart(selectedIndex: number) {
    if (this.chart) {
      // console.log('chriss ' + selectedIndex);

      this.chart.options.plugins!.highlightPlugin!.highlightIndex = selectedIndex;
      this.chart.update();
    }
  }

  onSubTypeDropDownSelect(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value as 'all' | 'grasp' | 'grip' | 'pinch' | 'gross';
    // console.log('Selected value:', selectedValue);
    this.createChart(selectedValue);
  }

  createChart(subType: 'all' | 'grasp' | 'grip' | 'pinch' | 'gross') {
    // jür jedes Datum eine Row
    const datasetsObject: AssessmentChartDataSets = this.dataFull() ?? {};
    const { minDate, maxDate, labelDataFull } = getMinMaxDate(datasetsObject);

    // data Array enthält für jeden Behandlungstermin eine Element, also eine Row
    // da steht dann immer der im Drop-Down selektierte subType score drin, je links und rechts
    const dataRows: Array<AssessmentChartDataRow> = this.data ?? [];
    // const assesmentType: AssessmentType = dataRows[0]?.type ?? 'ARAT';
    // const color = assessmentTypeColors[assesmentType];
    const data = dataRows.map(row => {
      let score_left = 0;
      let score_right = 0;
      let sum_left = 0;
      let sum_right = 0;
      let unitLabel = '';
      let unitLabel_left = '';
      let unitLabel_right = '';
      let hasComment: boolean = false;
      let comment: string | undefined;
      if (this.chartId === 'arat') {
        unitLabel = 'von 57 Punkten';
      }
      if (this.chartId === 'nhpt') {
        unitLabel = 'Sekunden';
      }
      if (this.chartId === 'bbs') {
        unitLabel = 'Punkte';
      }
      if (this.chartId === 'custom') {
        unitLabel = '';
      }

      // console.log('[chart] row', { row });

      if (subType !== 'all') {
        // zieh die daten von dem selektiereten subTest
        const subTest: SubTestChartDataSet = row.subDataSets.filter(set => set.subType === subType)[0];
        score_left = subTest.score_left!;
        score_right = subTest.score_right!;
        sum_left = subTest.sumLeft!;
        sum_right = subTest.sumRight!;
        hasComment = subTest.hasComment;
        comment = subTest.comment;
      } else {
        // den top-level score nehmen beim arat
        score_left = row.score_left!;
        score_right = row.score_right!;
        row.subDataSets.forEach(subDataSet => {
          const leftValue = subDataSet.sumLeft ?? 0;
          const rightValue = subDataSet.sumRight ?? 0;
          sum_left = sum_left + leftValue;
          sum_right = sum_right + rightValue;
          hasComment = hasComment || subDataSet.hasComment;
          if (subDataSet.comment) {
            comment = (comment ?? '') + '\n' + subDataSet.comment;
          }
          // Richtige Reihenfolge arat subtest comments
          if (this.chartId === 'arat') {
            const subTest1: SubTestChartDataSet = row.subDataSets.filter(set => set.subType === 'grasp')[0];
            const subTest2: SubTestChartDataSet = row.subDataSets.filter(set => set.subType === 'grip')[0];
            const subTest3: SubTestChartDataSet = row.subDataSets.filter(set => set.subType === 'pinch')[0];
            const subTest4: SubTestChartDataSet = row.subDataSets.filter(set => set.subType === 'gross')[0];
            comment = '';
            comment += subTest1.comment ? subTest1.comment + '\n' : '';
            comment += subTest2.comment ? subTest2.comment + '\n' : '';
            comment += subTest3.comment ? subTest3.comment + '\n' : '';
            comment += subTest4.comment ? subTest4.comment + '\n' : '';
          } else {
            if (this.chartId === 'bbt') {
              const norm_left = subDataSet.norm_left ?? 100;
              const norm_right = subDataSet.norm_right ?? 100;
              unitLabel_left = 'von ' + Math.round(norm_left).toLocaleString('de-DE') + ' Blöcken';
              unitLabel_right = 'von ' + Math.round(norm_right).toLocaleString('de-DE') + ' Blöcken';
            }
          }
        });
        if (this.chartId === 'custom') {
          sum_left = score_left;
          sum_right = score_right;
        }

        // // schlechtesten Score auswählen aus allen subTests
        // row.subDataSets.forEach(subDataSet => {
        //   const leftnew = subDataSet.score_left ?? 0;
        //   const rightnew = subDataSet.score_right ?? 0;
        //   if (leftnew > score_left) {
        //     score_left = leftnew;
        //   }
        //   if (rightnew > score_right) {
        //     score_right = rightnew;
        //   }
        //   const leftValue = subDataSet.sumLeft ?? 0;
        //   const rightValue = subDataSet.sumRight ?? 0;
        //   sum_left = sum_left + leftValue;
        //   sum_right = sum_right + rightValue;
        // });
      }

      hasComment = hasComment || row.hasComment;
      comment = comment || row.comment;
      // Invertierung Schädigung zu Erfüllung
      score_left = 100 - score_left;
      score_right = 100 - score_right;

      let tooltipLabelL = '';
      let tooltipLabelR = '';

      if (this.chartId === 'bbt') {
        if (score_left === 100 && sum_left === 0) {
          tooltipLabelL = ` L: keine Testung`;
        } else {
          tooltipLabelL = ` L: ${score_left} %  (${sum_left} ${unitLabel_left})`;
        }
        tooltipLabelR = ` R: ${score_right} %  (${sum_right} ${unitLabel_right})`;
      } else {
        tooltipLabelL = unitLabel !== '' ? ` L: ${score_left} %  (${sum_left} ${unitLabel})` : ` L: ${score_left} % `;
        tooltipLabelR =
          unitLabel !== '' ? ` R: ${score_right} %  (${sum_right} ${unitLabel})` : ` R: ${score_right} % `;
      }

      return {
        x: new Date(new Date(row.dateIso).toDateString()),
        dataL: score_left,
        dataR: score_right,
        hasComment,
        comment,
        tooltipLabelL,
        tooltipLabelR,
        documentId: row.documentId,
        assessmentType: row.type,
      };
    });

    // im multi-Line-chart haben wir immer nur 2 Datasets: eins für links und eins für rechts.
    // die liegen aber beide bereits in data. werden über den yAxisKey rausgefischt.
    const datasets = [
      {
        // label: 'links' + (this.patient.affectedSide === 'l' || this.patient.affectedSide === 'b' ? ' (betroffen)' : ''),
        label: 'links',
        data: data,
        parsing: {
          yAxisKey: 'dataL',
        },
        borderWidth: 2,
        tension: 0.2,
        borderColor: '#bde3ff',
        pointHitRadius: 20,
        pointStyle: customPointCanvasLeft,
        pointRadius: 10,
      },
      {
        label: 'rechts',
        data: data,
        parsing: {
          yAxisKey: 'dataR',
        },
        borderWidth: 2,
        tension: 0.1,
        borderColor: '#fcd19c',
        pointHitRadius: 20,
        pointStyle: customPointCanvasRight,
        pointRadius: 10,
      },
    ];

    const config = this.getChartConfig(datasets, minDate, maxDate, labelDataFull);

    if (this.chart) {
      this.chart.data = { labels: labelDataFull, datasets: datasets };
      this.chart.options.scales.x.min = minDate;
      this.chart.options.scales.x.max = maxDate;
      this.chart.update();
    } else {
      this.chart = new Chart(this.canvas.nativeElement, config);
      setTimeout(() => {
        this.canvas.nativeElement.addEventListener('click', (e: any) => {
          const points = this.chart.getElementsAtEventForMode(e, 'nearest', {}, true);
          const index = points?.length > 0 ? points[0].index : -1;
          // console.log('chris click ', { index, points, event: e, chart: this.chart });
          if (index > -1) {
            // console.log('chris win ' + this.chart.config.data.datasets[0].data[index].x);
            this.indexClicked.emit(index);
          }

          // const height = this.chart.scales.x.height;
          // const top = this.chart.scales.x.top;
          // const bottom = this.chart.scales.x.bottom;
          // const left = this.chart.scales.x.left;
          // const right = this.chart.scales.x.maxWidth / this.chart.scales.x.ticks.length;

          // const resetCoordinates = this.canvas.nativeElement.getBoundingClientRect();
          // const x = e.clientX - resetCoordinates.left;
          // const y = e.clientY - resetCoordinates.top;
          // console.log('chris 1--', { eX: e.clientX, eY: e.clientY, x, y, resetCoordinates });
          // console.log('chris 2++', { height, top, bottom, left, right, resetCoordinates });
          // for (let i = 0; i < this.chart.scales.x.ticks.length; i++) {
          //   if (x >= left + right * i && x <= right + right * i && y >= top && y <= bottom) {
          //     console.log('chris win ' + i, this.chart.config.data.datasets[0].data[i].x, this.chart.config.data);
          //   }
          // }
        });
      }, 250);
    }
  }

  getChartConfig(
    datasets: any,
    minDate: any,
    maxDate: any,
    labelDataFull: any
  ): ChartConfiguration | ChartConfigurationCustomTypesPerDataset {
    return {
      type: 'line',
      data: {
        labels: labelDataFull,
        datasets: datasets,
      },
      options: {
        interaction: {
          intersect: false,
          axis: 'xy',
          mode: 'index',
        },
        onClick: e => {
          const canvasPosition = getRelativePosition(e, this.chart);

          // Substitute the appropriate scale IDs
          const dataX = this.chart.scales.x.getValueForPixel(canvasPosition.x);
          const dataY = this.chart.scales.y.getValueForPixel(canvasPosition.y);
          // console.log('chris onClick', { dataX, dataY });
        },
        responsive: true,
        aspectRatio: 3,
        animation: {
          delay: 100,
        },
        clip: false,
        layout: {
          // autoPadding: true,
          //   padding: {
          //     left: -10,
          //     right: 10,
          //   },
        },
        scales: {
          y: {
            min: 0,
            max: 100,

            reverse: false,
            position: 'right',
            beginAtZero: true,
            grid: {
              display: true,
            },
            ticks: {
              stepSize: 25,
              callback: function (val, index) {
                return val + ' %';
              },
            },
          },
          x: {
            offset: true,
            grid: {
              display: true,
              // drawOnChartArea: true, // Zeichne Grid-Linien auch im Chartbereich
              // lineWidth: context => {
              //   // Zeichne stärkere Linien für Montage
              //   const date = new Date(context.tick.value);
              //   return date.getDay() === 1 ? 2 : 0.5; // Montag: Tag 1
              // },
              // color: context => {
              //   // Färbe Montags-Linien stärker
              //   const date = new Date(context.tick.value);
              //   return date.getDay() === 1 ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)';
              // },
            },
            ticks: {
              source: 'data',
              // minRotation: 20,
              autoSkip: true, // Alle Datumswerte anzeigen
              callback: function (value, index, ticks) {
                // console.log('ticks:', { value, index, ticks, datasets });
                const tickDate = dayjs(new Date(value));
                // return tickDate.format('dd, DD.MM');
                const tick = [tickDate.format('DD.MM'), tickDate.format('dd')];
                const reverseIndex = datasets[0].data!.length - 1 - index;
                if (datasets[0].data[reverseIndex]?.hasComment) {
                  tick.push(`💬`);
                }
                return tick;
              },
            },
            type: 'time',
            min: minDate,
            max: maxDate,
            time: {
              unit: 'day',
              isoWeekday: true,
              displayFormats: {
                week: 'dd, DD.MM',
              },
              tooltipFormat: 'dd, DD.MM.YYYY',
            },
          },
        },
        plugins: {
          title: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            bodyFont: {
              family: 'monospace',
              size: 12,
            },

            footerFont: {
              family: 'monospace',
              weight: 'normal',
              size: 12,
            },
            callbacks: {
              label: function (context) {
                const raw = context.raw as any;
                const yAxisKey = (context.dataset.parsing as any).yAxisKey;
                if (yAxisKey === 'dataL') {
                  return raw.tooltipLabelL;
                } else if (yAxisKey === 'dataR') {
                  return raw.tooltipLabelR;
                } else {
                  return raw.tooltipLabel;
                }
              },
              beforeFooter: function (context) {
                const raw = context[0].raw as any;
                const tooltip: string[] = [];
                if (raw.hasComment) {
                  tooltip.push('');
                  tooltip.push('💬 Kommentar zum Testergebnis');
                }

                return tooltip;
              },
              footer: function (context) {
                const raw = context[0].raw as any;
                if (raw.hasComment) {
                  return splitTextByMaxChars(raw.comment, 30);
                } else {
                  return [];
                }
              },
            },
          },
          legend: {
            display: false,
            position: 'top',
            align: 'start',
            labels: {
              usePointStyle: true,
            },
          },
          highlightPlugin: {
            highlightIndex: this.selectedIndex(), // Nutze die Komponente-Variable
            highlightWidth: 40,
            color: 'rgba(192, 192, 192, 0.2)',
          },
        },
      },
      plugins: [highlightPlugin], // this.xScaleImage, this.chartClickXLabel // this.customLegend, this.customCanvasBackgroundColor, this.jitterEffectsPlugin
    };
  }

  // nach Erfüllung (beste werte oben)
  drawChartBackgroundReverse(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(0.05, 'rgba(251,221,221,1)');

    gradientBack.addColorStop(0.05, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.51, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.51, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.75, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.75, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.96, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.96, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(1, 'rgba(255,255,255,1)');
  }

  xScaleImage = {
    id: 'xScaleImage',
    afterDraw: function (chart: Chart, args: any) {
      // console.log('afterDatasetsDraw', { argsMeta: args.meta, args, chart });
      const _args = args;
      const {
        ctx,
        data,
        chartArea: { bottom },
        scales: { x },
      } = chart;
      const dataIndex = _args.index;
      const meta: ChartMeta = _args.meta as ChartMeta;
      // meta.data[1].x -= 1;
      ctx.save();
      data.datasets[0].data.forEach((dataRow: any, index: any) => {
        // console.log('chris', { index, ix: x.getPixelForValue(index), ctx, data, bottom, x, dataRow });
        // console.log('chris', { index, ix: x.x, x });
        const label = new Image();
        label.src = 'assets/icons/icon-72x72.png';
        // ctx.drawImage(label, x.getPixelForValue(index), 10, 30, 30);
        ctx.drawImage(label, 10, bottom, 30, 30);
      });
    },
  };

  chartClickXLabel = {
    id: 'chartClickXLabel',
    afterEvent: (chart: Chart, event: any, opts: any) => {
      const evt = event.event;

      if (evt.type !== 'click') {
        return;
      }

      console.log('click', { chart, event, opts });

      // Canvas-Position des Klicks ermitteln
      const canvasPosition = getRelativePosition(evt, this.chart);
      const xAxis = chart.scales['x'];
      const yAxis = chart.scales['y'];
      console.log('click', { xAxis, yAxis, canvasPosition });

      // Prüfen, ob der Klick in der Nähe der x-Achse liegt
      if (canvasPosition.y > yAxis.bottom && canvasPosition.y < yAxis.bottom + 20) {
        // x-Wert (Datum) finden, das zum Klick gehört
        const dataX = xAxis.getValueForPixel(canvasPosition.x);
        console.log('Klick auf x-Achse:', { dataX });
      }

      const [found, labelInfo] = findLabel(getLabelHitBoxes(chart.scales['x']), evt);

      if (found) {
        console.log(labelInfo);
        // opts.listeners.click();
      }
    },
  };
  beforeDatasetDraw = {
    id: 'beforeDatasetDraw',
    beforeDatasetDraw: function (chart: Chart, args: any) {
      // console.log('jitterEffectsChris', { argsMeta: args.meta, args, chart });
      const _args = args;
      const dataIndex = _args.index;
      const meta: ChartMeta = _args.meta as ChartMeta;
      meta.data[1].x -= 1;
    },
  };

  jitterEffectsPlugin = {
    id: 'jitterEffects',
    beforeDatasetDraw: function (chart: Chart, args: any) {
      const _args = args,
        dataIndex = _args.index;
      const meta: ChartMeta = _args.meta as ChartMeta;
      const points = meta.data.map(function (el: any) {
        return {
          x: el.x,
          y: el.y,
        };
      });
      const dsLength = chart.data.datasets.length;
      const adjustedMap: any = []; // keeps track of adjustments to prevent double offsets

      // console.log('jitterEffectsPlugin', { data: chart.data, dsLength, adjustedMap, chart });
      for (let datasetIndex = 0; datasetIndex < dsLength; datasetIndex += 1) {
        if (dataIndex !== datasetIndex) {
          const datasetMeta = chart.getDatasetMeta(datasetIndex);
          datasetMeta.data.forEach(function (el: any) {
            const overlapFilter = points.filter(function (point: any) {
              return point.x === el.x && point.y === el.y;
            });

            let overlap = false;
            const overObj = JSON.parse(JSON.stringify(overlapFilter));
            for (const element of overObj) {
              if (element['x'] === el.x && element['y'] === el.y) {
                overlap = true;
                break;
              }
            }

            if (overlap) {
              el.x += 7;
              return;
              let adjusted = false;
              const adjustedFilter = adjustedMap.filter(function (item: any) {
                return item.datasetIndex === datasetIndex && item.dataIndex === dataIndex;
              });
              const adjObj = JSON.parse(JSON.stringify(adjustedFilter));
              for (const element of adjObj) {
                if (element['datasetIndex'] === datasetIndex && element['dataIndex'] === dataIndex) {
                  adjusted = true;
                  break;
                }
              }

              if (!adjusted && datasetIndex % 2) {
                el.x += 7;
              } else {
                el.x -= 7;
              }

              adjustedMap.push({
                datasetIndex: datasetIndex,
                dataIndex: dataIndex,
              });
              console.log('jitterEffects', { adjustedMap });
            }
          });
        }
      }
    },
  };

  customCanvasBackgroundColor = {
    id: 'customCanvasBackgroundColor',
    beforeDraw: (chart: any, args: any, options: any) => {
      const {
        ctx,
        chartArea: { left, right, top, bottom, width, height },
      } = chart;
      const canvas = chart.canvas;
      const chartArea = chart.chartArea;

      ctx.save();
      // Chart background
      const gradientBack = canvas.getContext('2d').createLinearGradient(right, bottom, right, top);
      this.drawChartBackgroundReverse(gradientBack);
      ctx.fillStyle = gradientBack;
      ctx.fillRect(
        chartArea.left,
        chartArea.bottom,
        chartArea.right - chartArea.left,
        chartArea.top - chartArea.bottom
      );
    },
  };
}
